import { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

const lessonTypes = [
  { value: 'INDIVIDUAL', label: 'Індивідуальний' },
  { value: 'PAIR', label: 'Парний' },
  { value: 'GROUP', label: 'Група' },
];

const paymentMethods = [
  { value: 'CASH', label: 'Готівка' },
  { value: 'BANK_TRANSFER', label: 'Переказ' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    '& fieldset': { borderColor: '#EDE9FE' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const schema = yup.object({
  paymentDate: yup.string().required('Оберіть дату оплати'),
  amount: yup.number().min(0).required('Сума обовʼязкова'),
  lessonType: yup.string().oneOf(lessonTypes.map((item) => item.value)).required(),
  paymentMethod: yup.string().oneOf(paymentMethods.map((item) => item.value)).required(),
  lessonsCovered: yup
    .number()
    .min(0, 'Має бути 0 або більше')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  notes: yup.string().nullable(),
});

const defaultValues = {
  paymentDate: dayjs().format('YYYY-MM-DD'),
  amount: '',
  lessonType: lessonTypes[0].value,
  paymentMethod: paymentMethods[0].value,
  lessonsCovered: 1,
  notes: '',
};

const PaymentFormDialog = ({ open, onClose, onSubmit, initialValues, title = 'Додати оплату', submitLabel = 'Зберегти' }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(initialValues || { ...defaultValues });
    }
  }, [initialValues, open, reset]);

  const onFormSubmit = handleSubmit(async (values) => {
    await onSubmit({
      ...values,
      amount: Number(values.amount),
      lessonsCovered: values.lessonsCovered === null || values.lessonsCovered === undefined ? null : Number(values.lessonsCovered),
      notes: values.notes || null,
    });
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 60px rgba(67,87,173,0.18)', overflow: 'hidden' } }}
    >
      <Box sx={{ background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, px: 3, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>{title}</Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stack spacing={2.5}>
          <Controller
            name="paymentDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Дата оплати"
                format="DD.MM.YYYY"
                value={field.value ? dayjs(field.value) : null}
                onChange={(val) => field.onChange(val ? val.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: Boolean(errors.paymentDate),
                    helperText: errors.paymentDate?.message,
                    sx: fieldSx,
                  },
                }}
              />
            )}
          />
          <TextField
            label="Сума (₴)"
            type="number"
            size="small"
            inputProps={{ min: 0 }}
            {...register('amount')}
            error={Boolean(errors.amount)}
            helperText={errors.amount?.message}
            sx={fieldSx}
          />
          <TextField
            label="Тип уроку"
            select
            size="small"
            {...register('lessonType')}
            error={Boolean(errors.lessonType)}
            helperText={errors.lessonType?.message}
            sx={fieldSx}
          >
            {lessonTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Спосіб оплати"
            select
            size="small"
            {...register('paymentMethod')}
            error={Boolean(errors.paymentMethod)}
            helperText={errors.paymentMethod?.message}
            sx={fieldSx}
          >
            {paymentMethods.map((method) => (
              <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Кількість занять"
            type="number"
            size="small"
            inputProps={{ min: 0 }}
            {...register('lessonsCovered')}
            error={Boolean(errors.lessonsCovered)}
            helperText={errors.lessonsCovered?.message}
            sx={fieldSx}
          />
          <TextField
            label="Примітки"
            size="small"
            multiline
            rows={2}
            {...register('notes')}
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>

      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#8891A5', fontWeight: 600, borderRadius: 3, px: 2.5 }}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={onFormSubmit}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, fontWeight: 700, px: 3 }}
        >
          {submitLabel}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default PaymentFormDialog;
